import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

const MARKER = '__mcaiModal';

/**
 * Browser/Android Back closes a full-screen modal instead of navigating the
 * page underneath it (ONB-23).
 *
 * While `visible`, one history entry is pushed for the modal. It copies the
 * router's own `history.state` (same URL, same entry id), so the router sees
 * the Back as a no-op and only this hook reacts to it. Closing the modal any
 * other way pops the entry again, so the next Back behaves normally.
 */
export function useCloseOnBrowserBack(visible: boolean, onClose: () => void, key = 'modal'): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (Platform.OS !== 'web' || !visible || typeof window === 'undefined') return undefined;
    const { history } = window;
    const token = `${key}:${Date.now()}`;
    let closedByBack = false;

    history.pushState({ ...(history.state || {}), [MARKER]: token }, '');

    const onPopState = () => {
      if (history.state?.[MARKER] === token) return;
      closedByBack = true;
      onCloseRef.current();
    };
    window.addEventListener('popstate', onPopState);

    return () => {
      window.removeEventListener('popstate', onPopState);
      // Closed with a button (not Back) and nothing navigated since: drop our entry.
      if (!closedByBack && history.state?.[MARKER] === token) {
        history.back();
      }
    };
  }, [key, visible]);
}
